/**
 * S7 view-as — the shell's half (seam S7, UX F5).
 *
 * The shell never *decides* it is in view-as. The mode is a server-side record
 * (an open `view_as_event`), and `/v1/me` carries it on every read — so the
 * shell reads the mode rather than remembering it. That is what makes "the
 * mode cannot be forgotten" true across a navigation, a reload and a second
 * tab: there is no client state to lose.
 */

import type { QueryClient } from "@tanstack/react-query";

import { API_BASE } from "./api";
import { probeMe } from "./shell";

export interface ViewAs {
  target_email: string;
  actor_email: string;
  display_name?: string;
}

/**
 * A first name from a Ketosource address — data we have, never invented.
 *
 * Falls back to the address's local part rather than guessing. A wrong name on
 * a banner about someone else's permissions is worse than an unglamorous one.
 */
function nameFor(view: ViewAs): string {
  if (view.display_name) return view.display_name;
  const local = view.target_email.split("@")[0] ?? view.target_email;
  const first = local.split(/[._-]/)[0] ?? local;
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : view.target_email;
}

/** Read the mode off a `/v1/me` payload. Absent and null both mean "not in view-as". */
export function viewAsFromMe(me: { view_as?: ViewAs | null } | undefined): ViewAs | null {
  return me?.view_as ?? null;
}

/** The persistent banner's line (UX §9 copy). Null when there is nothing to say. */
export function bannerText(view: ViewAs | null): string | null {
  if (!view) return null;
  return `Viewing as ${nameFor(view)} — read only.`;
}

/**
 * The inline refusal when a write is attempted in view-as.
 *
 * Deliberately the same sentence the API returns: one message, two entry
 * points, so the UI and the API never drift into two vocabularies.
 */
export function refusalText(view: ViewAs | null): string | null {
  if (!view) return null;
  return `You're viewing as ${nameFor(view)} — exit to act.`;
}

/** Open a view-as. Resolves to the error message on refusal, null on success. */
export async function startViewAs(targetEmail: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/v1/view-as`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_email: targetEmail }),
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  return body.message ?? "That view couldn't be opened.";
}

/** Close the view-as. Never blocked by the read-only rule — exiting must always work. */
export async function exitViewAs(): Promise<void> {
  await fetch(`${API_BASE}/v1/view-as`, { method: "DELETE", credentials: "include" });
}

/**
 * After a view-as opens or closes: show the NEW person's screens at once.
 *
 * The URL does not change, so nothing else would tell the browser or the query
 * cache that every entitlement-shaped answer they hold now belongs to someone
 * else. The nav is re-read past the browser's 60-second copy, and every other
 * query is reset so a module refetches as the new person instead of showing
 * the last person's data or refusal (bug 3f80402f).
 */
export async function rereadAfterViewAsChange(client: QueryClient): Promise<void> {
  // A plain `["me"]` read already in flight was served the old copy; cancel it so it
  // cannot land after this one and put the previous person back.
  await client.cancelQueries({ queryKey: ["me"] });
  const me = await probeMe(undefined, false, true);
  client.setQueryData(["me"], me);
  await client.resetQueries({ predicate: (query) => query.queryKey[0] !== "me" });
}
